/* ==========================================================================
   Basin — theme JavaScript
   Vanilla custom elements only. No frameworks, no polyfills, no dependencies.
   Every interactive component degrades to working HTML when JS is unavailable.
   ========================================================================== */

(function () {
  'use strict';

  const routes = (window.Shopify && window.Shopify.routes) || { root: '/' };
  const strings = (window.theme && window.theme.strings) || {};

  /* ------------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------------ */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  function trapFocus(container, event) {
    const focusable = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function announce(message) {
    const region = document.getElementById('LiveRegion');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 60);
  }

  function fetchJSON(url, options) {
    return fetch(url, options).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          const error = new Error(data.description || data.message || 'Request failed');
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  function sectionUrl(sections) {
    const base = routes.root || '/';
    const separator = base.indexOf('?') > -1 ? '&' : '?';
    return base + separator + 'sections=' + sections.join(',');
  }

  /**
   * Swaps the inner markup of `selector` with the matching region of a section
   * returned by the Section Rendering API. Only the region is replaced, so the
   * custom elements that wrap it stay alive and keep their event listeners.
   */
  function replaceRegion(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector(selector);
    const current = document.querySelector(selector);
    if (incoming && current) {
      current.innerHTML = incoming.innerHTML;
      return parsed;
    }
    return parsed;
  }

  /** Keeps every cart badge in the document in sync with the current count. */
  function updateCartCount(count) {
    if (count === null || count === undefined) return;
    const value = parseInt(count, 10) || 0;

    document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
      badge.textContent = value > 99 ? '99+' : String(value);
      badge.hidden = value === 0;
    });

    document.querySelectorAll('[data-cart-count-label]').forEach(function (label) {
      if (label.dataset.cartCountLabel) {
        label.textContent = label.dataset.cartCountLabel.replace('{{ count }}', value);
      }
    });
  }

  /* ------------------------------------------------------------------------
     Scroll lock — preserves scroll position and avoids layout shift
     ------------------------------------------------------------------------ */

  let lockCount = 0;
  let savedScroll = 0;

  function lockScroll() {
    lockCount += 1;
    if (lockCount > 1) return;
    savedScroll = window.scrollY;
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.top = '-' + savedScroll + 'px';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    if (scrollbar > 0) document.body.style.paddingRight = scrollbar + 'px';
    document.body.classList.add('overflow-hidden');
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';
    document.body.classList.remove('overflow-hidden');
    // 'instant' overrides the document's smooth scroll-behavior. The restore
    // must not animate: the page never visually moved while it was locked.
    window.scrollTo({ top: savedScroll, left: 0, behavior: 'instant' });
  }

  /* ------------------------------------------------------------------------
     Drawer — shared base for the menu drawer and cart drawer
     ------------------------------------------------------------------------ */

  class ThemeDrawer extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
      this.close = this.close.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('.drawer__panel');

      // Delegated so that re-rendered drawer contents stay closeable without
      // rebinding listeners after a Section Rendering API swap.
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
    }

    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);

      window.requestAnimationFrame(() => {
        const target =
          this.querySelector('[data-drawer-autofocus]') ||
          this.querySelector(FOCUSABLE) ||
          this.panel;
        if (target) target.focus({ preventScroll: true });
      });

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);

      if (this.opener && document.contains(this.opener)) {
        this.opener.focus({ preventScroll: true });
      }

      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'Tab') {
        trapFocus(this.panel || this, event);
      }
    }
  }

  /* ------------------------------------------------------------------------
     Menu drawer
     ------------------------------------------------------------------------ */

  class MenuDrawer extends ThemeDrawer {
    connectedCallback() {
      super.connectedCallback();

      document.querySelectorAll('[data-menu-drawer-open]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(button);
        });
      });
    }
  }

  /* ------------------------------------------------------------------------
     Cart — shared logic for the drawer and the cart page
     ------------------------------------------------------------------------ */

  /** Which cart-aware sections currently exist and should be re-rendered. */
  function cartSectionsToRender() {
    const ids = [];
    if (document.getElementById('shopify-section-cart-drawer')) ids.push('cart-drawer');
    if (document.getElementById('shopify-section-main-cart')) ids.push('main-cart');
    return ids;
  }

  function renderCartSections(sections) {
    if (!sections) {
      window.location.reload();
      return;
    }

    if (sections['cart-drawer']) {
      const parsed = replaceRegion(sections['cart-drawer'], '[data-cart-drawer-inner]');
      // The rendered drawer carries the authoritative count for add-to-cart,
      // whose response body is a line item rather than the whole cart.
      const source = parsed.querySelector('[data-cart-count-source]');
      if (source) updateCartCount(source.textContent.trim());
    }

    if (sections['main-cart']) {
      replaceRegion(sections['main-cart'], '[data-main-cart-inner]');
    }
  }

  function changeCartLine(key, quantity, origin) {
    if (!key) return;
    const item = origin ? origin.closest('.cart-item') : null;
    if (item) item.classList.add('is-loading');

    fetchJSON(routes.root + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        sections: cartSectionsToRender(),
        sections_url: window.location.pathname
      })
    })
      .then(function (cart) {
        renderCartSections(cart.sections);
        updateCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        if (quantity === 0) announce(strings.itemRemoved);
      })
      .catch(function (error) {
        if (item) {
          item.classList.remove('is-loading');
          const errorEl = item.querySelector('[data-cart-item-error]');
          if (errorEl) errorEl.textContent = error.message || strings.cartError;
        }
      });
  }

  /** Wires remove buttons and quantity inputs inside any cart container. */
  function attachCartControls(host) {
    host.addEventListener('click', function (event) {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      changeCartLine(remove.dataset.cartRemove, 0, remove);
    });

    host.addEventListener('change', function (event) {
      const input = event.target.closest('[data-cart-quantity-input]');
      if (!input) return;
      const quantity = parseInt(input.value, 10);
      if (Number.isNaN(quantity) || quantity < 0) {
        input.value = input.dataset.previousValue || 1;
        return;
      }
      changeCartLine(input.dataset.lineKey, quantity, input);
    });
  }

  class CartDrawer extends ThemeDrawer {
    connectedCallback() {
      super.connectedCallback();
      attachCartControls(this);

      document.querySelectorAll('[data-cart-drawer-open]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(button);
        });
      });

      document.addEventListener('cart:refresh', (event) => {
        renderCartSections(event.detail && event.detail.sections);
        if (event.detail && event.detail.open) this.open();
      });
    }
  }

  /** The cart page reuses the line-item controls without any drawer behaviour. */
  class CartPage extends HTMLElement {
    connectedCallback() {
      attachCartControls(this);
    }
  }

  /* ------------------------------------------------------------------------
     Quantity input
     ------------------------------------------------------------------------ */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.querySelectorAll('[data-quantity-step]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = parseInt(button.dataset.quantityStep, 10);
          const min = parseInt(this.input.min || '1', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + step));
          if (next === parseInt(this.input.value, 10)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.refresh();
        });
      });

      this.input.addEventListener('change', this.refresh.bind(this));
      this.refresh();
    }

    refresh() {
      const value = parseInt(this.input.value, 10) || 1;
      const min = parseInt(this.input.min || '1', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;

      this.querySelectorAll('[data-quantity-step]').forEach((button) => {
        const step = parseInt(button.dataset.quantityStep, 10);
        const next = value + step;
        button.disabled = next < min || next > max;
      });
    }
  }

  /* ------------------------------------------------------------------------
     Variant selector
     ------------------------------------------------------------------------ */

  class VariantSelector extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variant-json]');
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        this.variants = [];
        return;
      }

      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl !== 'false';

      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked')).map(
        (input) => input.value
      );
    }

    findVariant(options) {
      return this.variants.find(function (variant) {
        return variant.options.every(function (value, index) {
          return value === options[index];
        });
      });
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.findVariant(options);

      this.updateSelectedLabels();
      this.updateAvailability();

      const scope = document.getElementById('shopify-section-' + this.sectionId) || document;

      const idInput = scope.querySelector('[data-variant-id-input]');
      if (idInput && variant) idInput.value = variant.id;

      this.updatePrice(scope, variant);
      this.updateButtons(scope, variant);
      this.updateMedia(scope, variant);
      this.updateSku(scope, variant);

      if (this.updateUrl && variant && this.productUrl) {
        window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
      }

      document.dispatchEvent(
        new CustomEvent('variant:change', {
          detail: { variant: variant, sectionId: this.sectionId }
        })
      );
    }

    updateSelectedLabels() {
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        const display = group.querySelector('[data-option-selected]');
        if (checked && display) display.textContent = checked.value;
      });
    }

    updateAvailability() {
      const selected = this.selectedOptions;

      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        const index = parseInt(input.dataset.optionIndex, 10);
        const candidate = selected.slice();
        candidate[index] = input.value;

        const match = this.variants.find(function (variant) {
          return variant.options.every(function (value, i) {
            return i === index ? value === input.value : value === candidate[i];
          });
        });

        const available = Boolean(match && match.available);
        const label = input.nextElementSibling;
        if (label) label.classList.toggle('variant-label--unavailable', !available);
        if (label) {
          const note = label.querySelector('[data-availability-note]');
          if (note) note.textContent = available ? '' : ' (' + (strings.soldOut || 'Sold out') + ')';
        }
      });
    }

    updatePrice(scope, variant) {
      const container = scope.querySelector('[data-price-container]');
      if (!container) return;

      if (!variant) {
        container.setAttribute('hidden', '');
        return;
      }

      container.removeAttribute('hidden');

      const regular = container.querySelector('[data-price-regular]');
      const compare = container.querySelector('[data-price-compare]');
      const badge = container.querySelector('[data-price-badge]');
      const unit = container.querySelector('[data-price-unit]');

      if (regular) regular.textContent = variant.price;

      if (compare) {
        if (variant.compare_at_price) {
          compare.textContent = variant.compare_at_price;
          compare.removeAttribute('hidden');
        } else {
          compare.setAttribute('hidden', '');
        }
      }

      if (badge) {
        badge.toggleAttribute('hidden', !variant.compare_at_price);
      }

      if (unit) {
        if (variant.unit_price) {
          unit.textContent = variant.unit_price;
          unit.removeAttribute('hidden');
        } else {
          unit.setAttribute('hidden', '');
        }
      }

      const stickyPrice = document.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = variant.price;
    }

    updateButtons(scope, variant) {
      const buttons = scope.querySelectorAll('[data-add-to-cart]');
      const availability = scope.querySelector('[data-availability]');
      const paymentButton = scope.querySelector('.shopify-payment-button');

      buttons.forEach((button) => {
        const label = button.querySelector('[data-add-to-cart-label]');

        if (!variant) {
          button.disabled = true;
          if (label) label.textContent = strings.unavailable || 'Unavailable';
        } else if (!variant.available) {
          button.disabled = true;
          if (label) label.textContent = strings.soldOut || 'Sold out';
        } else {
          button.disabled = false;
          if (label) label.textContent = strings.addToCart || 'Add to cart';
        }
      });

      if (paymentButton) {
        paymentButton.style.display = variant && variant.available ? '' : 'none';
      }

      if (availability) {
        const available = Boolean(variant && variant.available);
        availability.textContent = available
          ? availability.dataset.inStock || ''
          : availability.dataset.outOfStock || '';
        availability.classList.toggle('product__availability--out', !available);
      }
    }

    updateSku(scope, variant) {
      const sku = scope.querySelector('[data-variant-sku]');
      if (!sku) return;
      if (variant && variant.sku) {
        sku.textContent = variant.sku;
        sku.closest('[data-variant-sku-wrapper]').removeAttribute('hidden');
      } else if (sku.closest('[data-variant-sku-wrapper]')) {
        sku.closest('[data-variant-sku-wrapper]').setAttribute('hidden', '');
      }
    }

    updateMedia(scope, variant) {
      if (!variant || !variant.featured_media_id) return;
      const gallery = scope.querySelector('product-gallery');
      if (gallery && typeof gallery.showMedia === 'function') {
        gallery.showMedia(String(variant.featured_media_id));
      }
    }
  }

  /* ------------------------------------------------------------------------
     Product form — adds to cart without leaving the page
     ------------------------------------------------------------------------ */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.button = this.querySelector('[data-add-to-cart]');
      this.errorEl = this.querySelector('[data-product-form-error]');
      this.cartType = (window.theme && window.theme.cartType) || 'drawer';

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      if (this.cartType !== 'drawer') return;
      if (!document.querySelector('cart-drawer')) return;

      event.preventDefault();
      if (this.button && this.button.disabled) return;

      this.setLoading(true);
      if (this.errorEl) {
        this.errorEl.textContent = '';
        this.errorEl.setAttribute('hidden', '');
      }

      const formData = new FormData(this.form);
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);

      fetchJSON(routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      })
        .then((response) => {
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              detail: { sections: response.sections, open: true }
            })
          );
          announce(strings.itemAdded);
        })
        .catch((error) => {
          if (this.errorEl) {
            this.errorEl.textContent = error.message || strings.cartError;
            this.errorEl.removeAttribute('hidden');
          }
          announce(error.message || strings.cartError);
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', loading);
      this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
      this.button.disabled = loading;
    }
  }

  /* ------------------------------------------------------------------------
     Product gallery
     ------------------------------------------------------------------------ */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.galleryThumb));
      });

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.scrollToIndex(index));
      });

      if (this.viewport && this.dots.length) {
        this.viewport.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      }
    }

    scrollToIndex(index) {
      const slide = this.slides[index];
      if (!slide || !this.viewport) return;
      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
    }

    showMedia(mediaId) {
      const index = this.slides.findIndex(function (slide) {
        return slide.dataset.mediaId === String(mediaId);
      });
      if (index === -1) return;

      this.setActive(index);

      const slide = this.slides[index];
      if (this.viewport && this.viewport.scrollWidth > this.viewport.clientWidth) {
        this.viewport.scrollTo({
          left: slide.offsetLeft - this.viewport.offsetLeft,
          behavior: 'smooth'
        });
      } else {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    onScroll() {
      if (this.scrollTimer) window.cancelAnimationFrame(this.scrollTimer);
      this.scrollTimer = window.requestAnimationFrame(() => {
        const center = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
        let closest = 0;
        let smallest = Infinity;

        this.slides.forEach((slide, index) => {
          const slideCenter = slide.offsetLeft - this.viewport.offsetLeft + slide.clientWidth / 2;
          const distance = Math.abs(slideCenter - center);
          if (distance < smallest) {
            smallest = distance;
            closest = index;
          }
        });

        this.setActive(closest);
      });
    }

    setActive(index) {
      if (!this.slides[index]) return;

      this.dots.forEach((dot, i) => {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      this.thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.galleryThumb === this.slides[index].dataset.mediaId;
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }
  }

  /* ------------------------------------------------------------------------
     Lightbox — shared by the gallery sections
     ------------------------------------------------------------------------ */

  class ImageLightbox extends HTMLElement {
    connectedCallback() {
      this.image = this.querySelector('[data-lightbox-image]');
      this.sources = [];
      this.index = 0;

      this.onKeydown = this.onKeydown.bind(this);

      this.querySelectorAll('[data-lightbox-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      const prev = this.querySelector('[data-lightbox-prev]');
      const next = this.querySelector('[data-lightbox-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));

      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      });
    }

    openWith(sources, index) {
      this.sources = sources;
      this.index = index || 0;
      this.render();
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      this.opener = document.activeElement;
      const close = this.querySelector('[data-lightbox-close]');
      if (close) close.focus({ preventScroll: true });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      if (this.opener && document.contains(this.opener)) {
        this.opener.focus({ preventScroll: true });
      }
    }

    step(delta) {
      if (this.sources.length < 2) return;
      this.index = (this.index + delta + this.sources.length) % this.sources.length;
      this.render();
    }

    render() {
      const source = this.sources[this.index];
      if (!source || !this.image) return;
      this.image.src = source.src;
      this.image.alt = source.alt || '';
      if (source.width) this.image.width = source.width;
      if (source.height) this.image.height = source.height;

      const counter = this.querySelector('[data-lightbox-counter]');
      if (counter) counter.textContent = this.index + 1 + ' / ' + this.sources.length;

      const nav = this.querySelectorAll('[data-lightbox-prev], [data-lightbox-next]');
      nav.forEach((button) => {
        button.hidden = this.sources.length < 2;
      });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowRight') {
        this.step(1);
      } else if (event.key === 'ArrowLeft') {
        this.step(-1);
      } else if (event.key === 'Tab') {
        trapFocus(this, event);
      }
    }
  }

  /* Gallery triggers wire themselves to the single shared lightbox */
  class LightboxGrid extends HTMLElement {
    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-lightbox-trigger]'));

      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          const lightbox = document.querySelector('image-lightbox');
          if (!lightbox) return;

          const sources = this.triggers.map(function (item) {
            return {
              src: item.dataset.lightboxSrc,
              alt: item.dataset.lightboxAlt,
              width: item.dataset.lightboxWidth,
              height: item.dataset.lightboxHeight
            };
          });

          lightbox.openWith(sources, index);
        });
      });
    }
  }

  /* ------------------------------------------------------------------------
     Accordion — animates native <details> without breaking no-JS behaviour
     ------------------------------------------------------------------------ */

  class ThemeAccordion extends HTMLElement {
    connectedCallback() {
      this.exclusive = this.dataset.exclusive === 'true';
      this.items = Array.from(this.querySelectorAll('details'));

      this.items.forEach((item) => {
        const summary = item.querySelector('summary');
        const panel = item.querySelector('.accordion__panel');
        if (!summary || !panel) return;

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          if (item.open) {
            this.collapse(item, panel);
          } else {
            if (this.exclusive) {
              this.items.forEach((other) => {
                if (other !== item && other.open) {
                  this.collapse(other, other.querySelector('.accordion__panel'));
                }
              });
            }
            this.expand(item, panel);
          }
        });
      });
    }

    expand(item, panel) {
      item.open = true;
      const height = panel.scrollHeight;
      panel.animate(
        { height: ['0px', height + 'px'], opacity: [0, 1] },
        { duration: 240, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
      );
    }

    collapse(item, panel) {
      const height = panel.scrollHeight;
      const animation = panel.animate(
        { height: [height + 'px', '0px'], opacity: [1, 0] },
        { duration: 200, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
      );
      animation.onfinish = function () {
        item.open = false;
      };
    }
  }

  /* ------------------------------------------------------------------------
     Sticky add to cart bar
     ------------------------------------------------------------------------ */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const targetSelector = this.dataset.watch;
      const target = targetSelector ? document.querySelector(targetSelector) : null;
      if (!target || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', passed);
            // Lets fixed elements (like the floating appearance toggle)
            // sit above the bar instead of underneath it.
            document.documentElement.style.setProperty(
              '--sticky-atc-height',
              passed ? this.offsetHeight + 'px' : '0px'
            );
          });
        },
        { rootMargin: '0px 0px 0px 0px', threshold: 0 }
      );

      this.observer.observe(target);

      const button = this.querySelector('[data-sticky-atc-button]');
      if (button) {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const form = document.querySelector(this.dataset.form || '[data-product-form] form');
          if (!form) return;
          // requestSubmit fires the submit event so the AJAX handler runs.
          // Older browsers fall back to a normal post, which still adds to cart.
          if (form.requestSubmit) {
            form.requestSubmit();
          } else {
            form.submit();
          }
        });
      }

      document.addEventListener('variant:change', (event) => {
        const variant = event.detail && event.detail.variant;
        const button = this.querySelector('[data-sticky-atc-button]');
        if (!button) return;
        button.disabled = !variant || !variant.available;
        const label = button.querySelector('[data-add-to-cart-label]');
        if (label) {
          label.textContent = variant && variant.available
            ? strings.addToCart || 'Add to cart'
            : strings.soldOut || 'Sold out';
        }
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  /* ------------------------------------------------------------------------
     Header behaviour — measures its own height and tracks scroll state
     ------------------------------------------------------------------------ */

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.measure();
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this);
      }

      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    measure() {
      // --header-height is a measured output for scroll-padding and sticky
      // offsets. Ignore sub-2px changes so ResizeObserver echo from our own
      // write can never re-trigger a measurement loop.
      const height = Math.round(this.offsetHeight);
      if (this.lastMeasuredHeight && Math.abs(height - this.lastMeasuredHeight) < 2) return;
      this.lastMeasuredHeight = height;
      document.documentElement.style.setProperty('--header-height', height + 'px');
    }

    onScroll() {
      this.classList.toggle('header-wrapper--scrolled', window.scrollY > 8);
    }
  }

  /* ------------------------------------------------------------------------
     Appearance toggle — site-wide light/dark switch, persisted per visitor
     ------------------------------------------------------------------------ */

  class AppearanceToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.button.addEventListener('click', () => {
        const dark = document.documentElement.getAttribute('data-appearance') === 'dark';

        // Animate the whole palette swap: colours cross-fade while the
        // day/night imagery does its own transition.
        document.documentElement.classList.add('theme-transitioning');
        window.clearTimeout(AppearanceToggle.transitionTimer);
        AppearanceToggle.transitionTimer = window.setTimeout(function () {
          document.documentElement.classList.remove('theme-transitioning');
        }, 650);

        this.apply(dark ? 'light' : 'dark');
      });

      // Keep every toggle instance (header + floating) in sync no matter
      // which one was pressed.
      this.attrObserver = new MutationObserver(() => this.sync());
      this.attrObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-appearance']
      });

      // Follow OS changes live, but only while the visitor has no explicit choice.
      this.media = window.matchMedia('(prefers-color-scheme: dark)');
      this.onMediaChange = (event) => {
        let stored = null;
        try {
          stored = localStorage.getItem('basin:appearance');
        } catch (error) {
          /* storage unavailable */
        }
        if (!stored) this.setAppearance(event.matches ? 'dark' : 'light');
      };
      this.media.addEventListener('change', this.onMediaChange);

      this.sync();
    }

    disconnectedCallback() {
      if (this.media) this.media.removeEventListener('change', this.onMediaChange);
      if (this.attrObserver) this.attrObserver.disconnect();
    }

    apply(mode) {
      try {
        localStorage.setItem('basin:appearance', mode);
      } catch (error) {
        /* storage unavailable */
      }
      this.setAppearance(mode);
    }

    setAppearance(mode) {
      document.documentElement.setAttribute('data-appearance', mode);
      this.sync();
    }

    sync() {
      const dark = document.documentElement.getAttribute('data-appearance') === 'dark';
      this.button.setAttribute('aria-pressed', String(dark));
      const label = dark ? this.button.dataset.labelToLight : this.button.dataset.labelToDark;
      if (label) this.button.setAttribute('aria-label', label);

      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) {
        window.requestAnimationFrame(() => {
          meta.setAttribute('content', getComputedStyle(document.body).backgroundColor);
        });
      }
    }
  }

  /* ------------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------------ */

  const STAGGER_GROUPS =
    '.benefit-strip__list, .gallery__grid, .steps, .testimonial-grid, .product-grid, .split__list';

  function initReveal(root) {
    const scope = root || document;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Grid children cascade in one after another instead of as a block.
    scope.querySelectorAll(STAGGER_GROUPS).forEach(function (group) {
      Array.from(group.children).forEach(function (child, index) {
        if (!child.classList.contains('reveal')) {
          child.classList.add('reveal');
          child.style.transitionDelay = Math.min(index * 70, 420) + 'ms';
        }
      });
    });

    const elements = scope.querySelectorAll('.reveal:not(.is-visible)');

    if (prefersReduced || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Registration
     ------------------------------------------------------------------------ */

  const components = {
    'theme-drawer': ThemeDrawer,
    'menu-drawer': MenuDrawer,
    'cart-drawer': CartDrawer,
    'cart-page': CartPage,
    'quantity-input': QuantityInput,
    'variant-selector': VariantSelector,
    'product-form': ProductForm,
    'product-gallery': ProductGallery,
    'image-lightbox': ImageLightbox,
    'lightbox-grid': LightboxGrid,
    'theme-accordion': ThemeAccordion,
    'sticky-atc': StickyAtc,
    'site-header': SiteHeader,
    'appearance-toggle': AppearanceToggle
  };

  Object.keys(components).forEach(function (name) {
    if (!customElements.get(name)) customElements.define(name, components[name]);
  });

  /* Scroll progress bar — a thin accent gradient across the top */
  function initScrollProgress() {
    const bar = document.querySelector('[data-scroll-progress]');
    if (!bar) return;
    let ticking = false;
    const update = function () {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.transform = 'scaleX(' + (max > 0 ? Math.min(1, window.scrollY / max) : 0) + ')';
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initReveal();
      initScrollProgress();
    });
  } else {
    initReveal();
    initScrollProgress();
  }

  /* Theme editor: re-run progressive enhancement when a section reloads */
  document.addEventListener('shopify:section:load', function (event) {
    initReveal(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    const drawer = event.target.querySelector('cart-drawer, menu-drawer');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    const drawer = event.target.querySelector('cart-drawer, menu-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });
})();
